import { formatLocalDate, parseLocalDateKey, toLocalDateKey } from '@/utils/localDateUtils';
import { useState, useEffect } from 'react';
import Modal from '../common/Modal';
import Button from '../common/Button';
import contractService from '@/services/contractService';
import clientService from '@/services/clientService';
import { Contrato, Cliente } from '@/types';
import { useDialog } from '@/hooks/useDialog';
import { getClientFullName } from '@/utils/clientUtils';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  contract: Contrato | null;
  onContractUpdated: () => void;
};

type TransferResult = {
  success: boolean;
  contrato_id: number;
  old_tenant_id: number;
  new_tenant_id: number;
  transfer_date: string;
  days_previous_tenant: number;
  partial_amount_previous: number;
  days_new_tenant: number;
  partial_amount_new: number;
};

export default function TransferContractModal({ isOpen, onClose, contract, onContractUpdated }: Props) {
  const { showAlert, showConfirm } = useDialog();
  const [form, setForm] = useState({
    new_tenant_id: '',
    transfer_date: toLocalDateKey(new Date()),
  });
  const [tenants, setTenants] = useState<Cliente[]>([]);
  const [loadingTenants, setLoadingTenants] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [transferResult, setTransferResult] = useState<TransferResult | null>(null);

  // Load tenants when modal opens
  useEffect(() => {
    if (isOpen && contract) {
      loadTenants();
      // Reset form
      setForm({
        new_tenant_id: '',
        transfer_date: toLocalDateKey(new Date()),
      });
      setTransferResult(null);
      setSearchTerm('');
    }
  }, [isOpen, contract]);

  const loadTenants = async () => {
    setLoadingTenants(true);
    try {
      // Backend uses 'estado: activo' to filter active clients, and 'tipo' for client type
      const response = await clientService.getClients({ tipo: 'INQUILINO', estado: 'activo', limit: 100 });
      const responseData = (response as any).data;
      // Handle paginated response: { items: [...], total: N, ... }
      const data = responseData?.items || responseData?.data || responseData || [];
      // Filter out current tenant
      const filtered = Array.isArray(data) ? data.filter((t: Cliente) => t.id !== contract?.inquilino_id) : [];
      setTenants(filtered);
    } catch (error) {
      console.error('Error loading tenants:', error);
    } finally {
      setLoadingTenants(false);
    }
  };

  // Calculate partial payment preview
  const calculatePreview = () => {
    if (!form.transfer_date || !contract) return null;

    const fecha = parseLocalDateKey(form.transfer_date);
    if (!fecha) return null;
    const dayOfMonth = fecha.getDate();
    const daysInMonth = new Date(fecha.getFullYear(), fecha.getMonth() + 1, 0).getDate();

    const daysOccupiedPrevious = dayOfMonth;
    const daysOccupiedNew = daysInMonth - dayOfMonth;

    const monthlyAmount = Number(contract.monto_alquiler_inquilino);
    const partialAmountPrevious = Math.round((monthlyAmount / 30) * daysOccupiedPrevious * 100) / 100;
    const partialAmountNew = Math.round((monthlyAmount / 30) * daysOccupiedNew * 100) / 100;

    return {
      daysOccupiedPrevious,
      daysOccupiedNew,
      partialAmountPrevious,
      partialAmountNew,
      daysInMonth,
    };
  };

  const preview = calculatePreview();

  const handleSubmit = async () => {
    if (!form.new_tenant_id) {
      showAlert({
        type: 'warning',
        title: 'Campo requerido',
        message: 'Por favor seleccione un nuevo inquilino',
      });
      return;
    }

    if (!form.transfer_date) {
      showAlert({
        type: 'warning',
        title: 'Campo requerido',
        message: 'Por favor seleccione la fecha de traspaso',
      });
      return;
    }

    const selectedTenant = tenants.find(t => t.id === Number(form.new_tenant_id));
    const confirmMessage = `¿Está seguro que desea traspasar el contrato?\n\n` +
      `Inquilino actual: ${contract?.inquilino ? getClientFullName(contract.inquilino) : 'N/A'}\n` +
      `Nuevo inquilino: ${selectedTenant ? getClientFullName(selectedTenant) : 'N/A'}\n` +
      `Fecha de traspaso: ${formatLocalDate(form.transfer_date)}\n\n` +
      `Se generarán pagos parciales para ambos inquilinos.`;

    const confirmed = await showConfirm({
      title: 'Confirmar Traspaso',
      message: confirmMessage,
      type: 'warning',
      confirmText: 'Sí, traspasar',
    });
    if (!confirmed) return;

    setSubmitting(true);
    try {
      const response = await contractService.transferContract(Number(contract?.id), {
        new_tenant_id: Number(form.new_tenant_id),
        transfer_date: form.transfer_date,
      });

      const result = (response as any).data as TransferResult;
      setTransferResult(result);
    } catch (error: any) {
      console.error('Error transferring contract:', error);
      showAlert({
        type: 'error',
        title: 'Error',
        message: error.response?.data?.message || 'Error al traspasar el contrato',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (transferResult) {
      onContractUpdated();
    }
    setTransferResult(null);
    onClose();
  };

  // Filter tenants by search term
  const filteredTenants = tenants.filter(tenant => {
    if (!searchTerm) return true;
    const fullName = getClientFullName(tenant).toLowerCase();
    const documento = tenant.documento?.toLowerCase() || '';
    const search = searchTerm.toLowerCase();
    return fullName.includes(search) || documento.includes(search);
  });

  if (!contract) return null;

  // Show success result
  if (transferResult) {
    const oldTenant = contract.inquilino;
    const newTenant = tenants.find(t => t.id === transferResult.new_tenant_id);

    return (
      <Modal isOpen={isOpen} onClose={handleClose} title="Traspaso Completado" size="md">
        <div className="space-y-4">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center gap-2 text-green-800 font-medium mb-2">
              <span className="text-xl">✓</span>
              <span>Traspaso realizado exitosamente</span>
            </div>
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
            <h4 className="font-medium text-gray-900">Resumen del Traspaso</h4>

            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-500">Inquilino Anterior</p>
                <p className="font-medium text-gray-900">
                  {oldTenant ? getClientFullName(oldTenant) : `ID: ${transferResult.old_tenant_id}`}
                </p>
              </div>
              <div>
                <p className="text-gray-500">Nuevo Inquilino</p>
                <p className="font-medium text-gray-900">
                  {newTenant ? getClientFullName(newTenant) : `ID: ${transferResult.new_tenant_id}`}
                </p>
              </div>
            </div>

            <div className="border-t border-gray-200 pt-3 mt-3">
              <p className="text-gray-500 text-sm mb-2">Fecha de Traspaso</p>
              <p className="font-medium text-gray-900">
                {formatLocalDate(transferResult.transfer_date)}
              </p>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-medium text-blue-900 mb-3">Pagos Parciales Generados</h4>

            <div className="space-y-3">
              <div className="bg-white rounded-md p-3 border border-blue-200">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm text-gray-600">Inquilino Anterior ({transferResult.days_previous_tenant} días)</p>
                    <p className="font-medium text-gray-900">
                      {oldTenant ? getClientFullName(oldTenant) : 'Anterior'}
                    </p>
                  </div>
                  <p className="text-lg font-bold text-blue-700">
                    {contract.moneda_inquilino} {transferResult.partial_amount_previous.toLocaleString('es-UY', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-md p-3 border border-blue-200">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm text-gray-600">Nuevo Inquilino ({transferResult.days_new_tenant} días)</p>
                    <p className="font-medium text-gray-900">
                      {newTenant ? getClientFullName(newTenant) : 'Nuevo'}
                    </p>
                  </div>
                  <p className="text-lg font-bold text-green-700">
                    {contract.moneda_inquilino} {transferResult.partial_amount_new.toLocaleString('es-UY', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end pt-4 border-t">
            <Button onClick={handleClose}>
              Cerrar
            </Button>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Traspaso de Contrato" size="lg">
      <div className="space-y-4">
        {/* Info Banner */}
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
          <p className="text-sm text-purple-800">
            <strong>Traspaso:</strong> Permite cambiar el inquilino de un contrato activo.
            Se generarán pagos parciales proporcionales para el inquilino saliente y entrante.
          </p>
        </div>

        {/* Current Contract Info */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <h4 className="font-medium text-gray-900 mb-3">Información del Contrato</h4>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-gray-500">Contrato</p>
              <p className="font-medium text-gray-900">{contract.numero_contrato}</p>
            </div>
            <div>
              <p className="text-gray-500">Inmueble</p>
              <p className="font-medium text-gray-900">{contract.inmueble?.direccion || 'N/A'}</p>
            </div>
            <div>
              <p className="text-gray-500">Inquilino Actual</p>
              <p className="font-medium text-gray-900">
                {contract.inquilino ? getClientFullName(contract.inquilino) : 'N/A'}
              </p>
            </div>
            <div>
              <p className="text-gray-500">Alquiler Mensual</p>
              <p className="font-medium text-gray-900">
                {contract.moneda_inquilino} {Number(contract.monto_alquiler_inquilino).toLocaleString()}
              </p>
            </div>
          </div>
        </div>

        {/* Transfer Date */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Fecha de Traspaso <span className="text-red-500">*</span>
          </label>
          <input
            type="date"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
            value={form.transfer_date}
            onChange={(e) => setForm({ ...form, transfer_date: e.target.value })}
            required
          />
          <p className="text-xs text-gray-500 mt-1">
            Fecha en que el nuevo inquilino toma posesión del inmueble
          </p>
        </div>

        {/* Tenant Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Nuevo Inquilino <span className="text-red-500">*</span>
          </label>

          {/* Search */}
          <input
            type="text"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 mb-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
            placeholder="Buscar por nombre o documento..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />

          {/* Tenant List */}
          <div className="border border-gray-300 rounded-lg max-h-48 overflow-y-auto">
            {loadingTenants ? (
              <div className="p-4 text-center text-gray-500">Cargando inquilinos...</div>
            ) : filteredTenants.length === 0 ? (
              <div className="p-4 text-center text-gray-500">
                {searchTerm ? 'No se encontraron inquilinos' : 'No hay inquilinos disponibles'}
              </div>
            ) : (
              filteredTenants.map((tenant) => (
                <div
                  key={tenant.id}
                  onClick={() => setForm({ ...form, new_tenant_id: String(tenant.id) })}
                  className={`p-3 border-b border-gray-200 last:border-b-0 cursor-pointer transition-colors ${
                    form.new_tenant_id === String(tenant.id)
                      ? 'bg-purple-100 border-purple-300'
                      : 'hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900">{getClientFullName(tenant)}</p>
                      <p className="text-sm text-gray-500">
                        {tenant.tipo_documento} {tenant.documento}
                      </p>
                    </div>
                    {form.new_tenant_id === String(tenant.id) && (
                      <span className="text-purple-600 text-lg">✓</span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Partial Payment Preview */}
        {preview && form.transfer_date && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h4 className="font-medium text-yellow-900 mb-3">
              Vista Previa de Pagos Parciales
            </h4>
            <p className="text-xs text-yellow-700 mb-3">
              Fórmula: (monto mensual / 30) × días ocupados
            </p>

            <div className="space-y-2 text-sm">
              <div className="flex justify-between items-center bg-white rounded p-2">
                <div>
                  <span className="text-gray-700">Inquilino Actual</span>
                  <span className="text-gray-500 ml-2">({preview.daysOccupiedPrevious} días)</span>
                </div>
                <span className="font-bold text-gray-900">
                  {contract.moneda_inquilino} {preview.partialAmountPrevious.toLocaleString('es-UY', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}
                </span>
              </div>

              <div className="flex justify-between items-center bg-white rounded p-2">
                <div>
                  <span className="text-gray-700">Nuevo Inquilino</span>
                  <span className="text-gray-500 ml-2">({preview.daysOccupiedNew} días)</span>
                </div>
                <span className="font-bold text-gray-900">
                  {contract.moneda_inquilino} {preview.partialAmountNew.toLocaleString('es-UY', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}
                </span>
              </div>

              <div className="border-t border-yellow-200 pt-2 mt-2">
                <p className="text-xs text-yellow-700">
                  Días en el mes: {preview.daysInMonth} |
                  Base de cálculo: 30 días
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3 justify-end pt-4 border-t">
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={submitting}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || !form.new_tenant_id || !form.transfer_date}
          >
            {submitting ? 'Procesando...' : 'Realizar Traspaso'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
